// ============================================================================
// landmark（檢查位置）→ 縮寫
//
// 截圖卡片的第二列是「大小　位置」，位置只分得到約 12 個字元的寬度，而
// landmarks 主檔最長 25 字（`proximal transverse colon`）。這支負責把它縮到
// 看得懂又放得下。
//
// ── 為什麼是「規則 + 例外」而不是 74 筆手工對照表 ────────────────────────
//
// landmarks 主檔有胃鏡 43 / 大腸鏡 31 / 乙狀結腸鏡 30 筆（去重後 74 個名稱）。
// 手工表要嘛一開始就漏，要嘛醫院端新增一個 landmark 之後忘了補、卡片直接顯示
// 25 字的全名把版面撐爛。規則能自動涵蓋新增的名稱，例外表只收「規則猜錯的」。
//
// ── 規則 ──────────────────────────────────────────────────────────────────
//
//   最後一個字保留；其餘每個字：
//     · 長度 ≥ 4 且純字母  → 大寫首字母 + '.'（ascending → A.）
//     · 其餘（2~3 字母、含數字）→ 原樣，純字母者轉大寫（aw → AW、2nd → 2nd）
//
//   每個縮寫仍保留詞界空格，例 `A. colon`、`P. T. colon`、`AW antrum`。
//
//   長度門檻是這條規則的關鍵：胃鏡的 `aw / gc / lc / pw` 是解剖方位的既有縮寫
//   （anterior wall / greater curvature / lesser curvature / posterior wall），
//   把它們再縮成 `a.` / `g.` 會直接失去意義，而且 `aw antrum` 與 `angle` 都會
//   變成 `a.`。所以短字保留原樣，只轉大寫讓它看起來像縮寫。
//
// ⚠️ 這是**顯示用**的轉換，單向且不保證可逆（`A. colon` 反推不出是 ascending
//    還是 anal）。任何需要知道「到底是哪個 landmark」的地方一律用 landmarkID，
//    不要拿縮寫字串回頭比對。
// ============================================================================

/**
 * 整串覆蓋。規則跑出來難看或不合臨床慣例的才收進來，不是全表。
 *
 * key 一律是正規化後的小寫（底線已換成空白），見 normalize()。
 */
const WHOLE_OVERRIDES = {
  // 規則會給 `ALL s.OF colon`——四個字的名稱本來就不適合逐字縮
  'all segments of colon': 'all colon',

  // 以下五個有通用的臨床寫法，醫師看縮寫比看規則產物快。
  // 規則本來會給 h.flexure / s.flexure / i.valve / a.orifice / RS junction，
  // 都看得懂，只是不是大家平常寫的那個。要改回規則產物就把這幾行刪掉。
  'hepatic flexure': 'HF',
  'splenic flexure': 'SF',
  'ileocecal valve': 'ICV',
  'appendix orifice': 'AO',
  'rs junction': 'RSJ',
}

/**
 * 單字覆蓋。與 WHOLE_OVERRIDES 的差別：這裡是逐字替換，所以一條就能同時服務
 * `aw upper-body` / `gc upper-body` / `lc upper-body` / `pw upper-body`。
 *
 * 胃鏡的 body 分段（`gc upper-body` = 13 字）是唯一超出卡片寬度的一組，
 * 縮掉後綴之後 `GC u-body` 只有 9 字。
 */
const TOKEN_OVERRIDES = {
  'upper-body': 'u-body',
  'high-body': 'h-body',
  'mid-body': 'm-body',
  'low-body': 'l-body',
}

/** 規則的縮寫門檻。低於這個長度的字保留原樣（見檔頭關於 aw/gc/lc/pw 的說明） */
const ABBREVIATE_MIN_LEN = 4

const ALPHA_RE = /^[a-z]+$/

/** 主檔混用了 `appendix orifice` 與 `appendix_orifice` 兩種寫法，先統一 */
function normalize(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function abbreviateHeadToken(token) {
  if (TOKEN_OVERRIDES[token]) return TOKEN_OVERRIDES[token]
  if (token.length >= ABBREVIATE_MIN_LEN && ALPHA_RE.test(token)) {
    return `${token[0].toUpperCase()}.`
  }
  // 2~3 字母的既有縮寫轉大寫；`2nd` 這種含數字的原樣保留（轉大寫會變成 2ND）
  return ALPHA_RE.test(token) ? token.toUpperCase() : token
}

/**
 * landmark 名稱 → 卡片用的縮寫。
 *
 * @param {string} name landmarks.landmark 的原始值
 * @returns {string} 縮寫；空值進來就回空字串
 *
 * @example
 *   landmarkAbbr('ascending colon')          // 'A. colon'
 *   landmarkAbbr('proximal transverse colon')// 'P. T. colon'
 *   landmarkAbbr('aw antrum')                // 'AW antrum'
 *   landmarkAbbr('hepatic flexure')          // 'HF'
 *   landmarkAbbr('rectum')                   // 'rectum'（單字不動）
 */
export function landmarkAbbr(name) {
  const normalized = normalize(name)
  if (!normalized) return ''
  if (WHOLE_OVERRIDES[normalized]) return WHOLE_OVERRIDES[normalized]

  const tokens = normalized.split(' ')
  // 單字本來就短（cecum / rectum / anus / ecj / body…），縮了反而看不懂
  if (tokens.length === 1) return TOKEN_OVERRIDES[tokens[0]] || tokens[0]

  const last = TOKEN_OVERRIDES[tokens[tokens.length - 1]] || tokens[tokens.length - 1]
  const head = tokens.slice(0, -1).map(abbreviateHeadToken)

  return [...head, last].join(' ')
}

/**
 * 從 landmark 清單找出某個 id 的縮寫。卡片只拿得到 screenshots.landmarkID，
 * 名稱要回 landmarks 主檔查（store 的 examinations/landmarkList）。
 *
 * @param {Array<{id:number, landmark:string}>} list
 * @param {number|string|null} landmarkID  0 / null 都代表未指定
 * @returns {string} 找不到就回空字串——卡片該留白，不該顯示 'undefined'
 */
export function landmarkAbbrById(list, landmarkID) {
  const id = Number(landmarkID)
  if (!id || !Array.isArray(list)) return ''
  const hit = list.find((l) => Number(l.id) === id)
  return hit ? landmarkAbbr(hit.landmark) : ''
}
